import { useState } from 'react';
import { Modal, Stack, Group, Button, Text } from '@mantine/core';
import { Calendar } from 'react-feather';
import { DateTimeField } from '../../../../components/common';
import { DEFAULT_START_TIME_ON_DATE_PICK } from '../../../../utils/sessionEndTime';

interface AddHuddleModalProps {
  opened: boolean;
  onClose: () => void;
  onAdd: (date: string | null) => Promise<void>;
}

/**
 * Modal for adding a new Coach Huddle
 * Date/time is optional - huddle can be added as "unscheduled"
 */
export function AddHuddleModal({ opened, onClose, onAdd }: AddHuddleModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    setLoading(true);
    try {
      // Pass the date as ISO string, or null if unscheduled
      await onAdd(selectedDate ? selectedDate.toISOString() : null);
      handleClose();
    } catch {
      // Error is handled by parent, but keep modal open for retry
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <Calendar size={18} />
          <Text fw={600}>Add coach huddle</Text>
        </Group>
      }
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Add a new coach huddle. You can optionally set a date and time, or leave it unscheduled to
          set later.
        </Text>

        <DateTimeField
          defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
          label="Date & Time (optional)"
          placeholder="Select date and time"
          value={selectedDate}
          onChange={setSelectedDate}
          minDate={new Date()}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleAdd} loading={loading}>
            Add huddle
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddHuddleModal;
